import {
  FormHelperText,
  MenuItem,
  Select,
  SxProps,
  Theme,
} from '@mui/material';
import { ReactNode } from 'react';
import { ControllerRenderProps, FieldValues, Path } from 'react-hook-form';
import ArrowDownIcon from '../../../../../components/Icons/ArrowDownIcon';
import formatEnumValue from '../../../../../utils/formatEnumValue';
import { hasProperty } from './helper/utility';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
    sx: {
      '& .MuiMenu-list': {
        paddingTop: '0px',
        paddingBottom: '0px',
      },
    },
  },
};

interface FlexibleSingleSelectProps<
  T extends FieldValues,
  K extends Array<any>,
> {
  label: string;
  field?: ControllerRenderProps<T, Path<T>>;
  selectItems: K;
  placeholder: string;
  value?: string;
  onChange?: (value: string) => void;
  helperText?: string;
}

const FlexibleSingleSelect = <T extends FieldValues, K extends Array<any>>({
  label,
  field,
  selectItems,
  placeholder,
  value,
  onChange,
  helperText,
}: FlexibleSingleSelectProps<T, K>) => {
  const {
    onChange: onFieldValueChange,
    value: fieldValue,
    ...restFieldProps
  } = field || {};

  // Always provide a defined value to MUI Select to keep it controlled
  const selectValue =
    typeof fieldValue !== 'undefined'
      ? (fieldValue as string)
      : typeof value !== 'undefined'
        ? value
        : '';

  return (
    <>
      <Select
        {...restFieldProps}
        value={selectValue}
        displayEmpty={true}
        IconComponent={(props) => <ArrowDownIcon {...props} />}
        renderValue={(selected) => {
          if (!selected) {
            return placeholder;
          }
          return formatEnumValue(selected);
        }}
        onChange={(event) => {
          const { value } = event.target;
          if (typeof value === 'string') {
            onChange?.(value);
          }
          onFieldValueChange?.(value);
        }}
        sx={selectSx}
        MenuProps={MenuProps}
        inputProps={{ 'aria-label': 'Without label' }}
      >
        {selectItems.map((item) => (
          <MenuItem
            key={item.id}
            value={item.id}
            sx={{ fontSize: '14px', fontWeight: 400 }}
          >
            {hasProperty(item, label) ? (item[label] as ReactNode) : item.id}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText id="helper-text" sx={{ color: 'primary.main' }}>
        {helperText}
      </FormHelperText>
    </>
  );
};

export default FlexibleSingleSelect;

const selectSx: SxProps<Theme> = (theme) => ({
  '& .MuiSelect-select.MuiSelect-outlined.MuiSelect-multiple.MuiInputBase-input.MuiOutlinedInput-input':
    {
      display: 'flex',
      alignItems: 'center',
      height: 'fit-content',
      p: '12px 25px 12px 14px',
    },
  '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input':
    {
      alignItems: 'center',
      height: 'fit-content',
      p: '12px 25px 12px 14px',
    },
  '& .MuiSelect-select:focus': {
    outline: 'none',
  },
  '& .MuiSelect-select:focusWithin': {
    outline: 'none',
  },
  '& .MuiSelect-icon': {
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
  boxShadow: 'none',
  '.MuiOutlinedInput-notchedOutline': { border: 0 },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 0,
  },
});
